import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_STREAM_PLATFORMS } from '@ygoargentina/helpers/stream-destinations';
import { getGraphQLQueryClient } from './query-client';
import type { StreamPlatform } from './stream-sessions';

export interface AppConfig {
  maintenance: boolean;
  maintenanceTitle: string | null;
  maintenanceMessage: string | null;
  maintenanceEndsAt: string | null;
}

export const appConfigQueryKey = ['appConfig'] as const;

export const getAppConfig = async (): Promise<AppConfig> => {
  const res = await getGraphQLQueryClient(null).request<{
    appConfig: AppConfig;
  }>(gql`
    query appConfig {
      appConfig {
        maintenance
        maintenanceTitle
        maintenanceMessage
        maintenanceEndsAt
      }
    }
  `);

  return res.appConfig;
};

export interface MaintenanceStatus {
  maintenance: boolean;
  title: string | null;
  message: string | null;
  endsAt: string | null;
  isLoading: boolean;
}

/**
 * Polls the public appConfig query every 60s. Fail-open: on error/loading it
 * reports maintenance=false so a network blip never locks users out.
 */
export const useMaintenanceStatus = (): MaintenanceStatus => {
  const { data, isLoading } = useQuery({
    queryKey: appConfigQueryKey,
    queryFn: getAppConfig,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  });

  return {
    maintenance: data?.maintenance ?? false,
    title: data?.maintenanceTitle ?? null,
    message: data?.maintenanceMessage ?? null,
    endsAt: data?.maintenanceEndsAt ?? null,
    isLoading,
  };
};

export const streamPlatformsQueryKey = [...appConfigQueryKey, 'streamPlatforms'] as const;

/**
 * Plataformas habilitadas para agregar destinos de transmisión nuevos. Query
 * aparte de la del mantenimiento: un API que todavía no tiene el campo rompe
 * sólo esto, no el aviso de mantenimiento.
 */
export const getStreamPlatforms = async (): Promise<StreamPlatform[]> => {
  const res = await getGraphQLQueryClient(null).request<{
    appConfig: { streamPlatforms: StreamPlatform[] };
  }>(gql`
    query appConfigStreamPlatforms {
      appConfig {
        streamPlatforms
      }
    }
  `);

  return res.appConfig.streamPlatforms;
};

/**
 * Mientras carga o si falla, las de por defecto: el API rechaza igual las
 * apagadas, así que esto sólo decide qué ofrece el menú.
 */
export const useStreamPlatforms = (): StreamPlatform[] => {
  const { data } = useQuery({
    queryKey: streamPlatformsQueryKey,
    queryFn: getStreamPlatforms,
    staleTime: 5 * 60_000,
    retry: false,
  });

  return data ?? (DEFAULT_STREAM_PLATFORMS as StreamPlatform[]);
};
